import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTableSearchParams } from '@/hooks/useTableSearchParams';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Tooltip,
  Snackbar,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Delete as DeleteIcon,
  Help,
  Refresh as RefreshIcon,
  RestartAlt as RetriggerIcon,
  PlayArrow as StartClassifyIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import { apiClient } from '@/lib/api';
import type { PathogenClassificationResult } from '@/services/api/testService';
import { TestStatusBadge } from '@/components/Tests/TestStatusBadge';
import { canOpenTestResults, getTestOpeningBlockReason, canRetriggerClassification, canStartClassification } from '@/lib/testStatus';
import { DeleteTestDialog } from '@/components/Tests/DeleteTestDialog';
import { useUser, useHasAnyRole } from '@/store';
import { TestChartOverlay } from './components/TestChartOverlay';
import { profileService } from '@/services/api/profileService';
import { usersApi } from '@/features/users/api/usersApi';
import { devicesApiService } from '@/services/devicesApiService';
import { TestStatus, TestStatusLabels } from '@/types/test';
import { ColumnFilterMenu } from './components/ColumnFilterMenu';

interface Test {
  id: string;
  status: string;
  classificationStatus?: string;
  profileName?: string;
  operatorName?: string;
  deviceNickname?: string;
  deviceUid?: string;
  startedAt?: string;
  completedAt?: string;
  durationMs?: number;
  pathogenClassificationResults?: PathogenClassificationResult[];
}

interface PagedResult {
  items: Test[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

const getStatusIcon = (status?: string | null) => {
  switch (status) {
    case 'positive':
      return <CheckCircle fontSize="small" />;
    case 'negative':
      return <Cancel fontSize="small" />;
    case 'inconclusive':
      return <Help fontSize="small" />;
    default:
      return null;
  }
};

const getStatusColor = (
  status?: string | null
): 'error' | 'success' | 'default' | 'primary' => {
  switch (status) {
    case 'positive':
      return 'error'; // red
    case 'negative':
      return 'success'; // green
    case 'inconclusive':
      return 'default'; // grey
    default:
      return 'primary'; // blue (pending)
  }
};

function formatDateTime(dateStr?: string): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString();
}

function formatDuration(durationMs?: number): string {
  if (!durationMs) return '-';
  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

export function TestsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const user = useUser();
  const canDeleteTests = user?.role === 'admin' || user?.role === 'supervisor';
  const canManageClassification = user?.role === 'admin' || user?.role === 'supervisor';
  const canViewChart = useHasAnyRole('researcher', 'admin');
  const [retriggering, setRetriggering] = useState<string | null>(null);
  const [startingClassification, setStartingClassification] = useState<string | null>(null);
  const [chartOverlayTestId, setChartOverlayTestId] = useState<string | null>(null);
  const { page, pageSize, setPage, setPageSize, getParam, setParams, setSelected } = useTableSearchParams({ pageSize: 10 });
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; testId: string }>(
    { open: false, testId: '' }
  );
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>(
    { open: false, message: '', severity: 'success' }
  );

  const sortBy = getParam('sortBy');
  const sortDir = (getParam('sortDir') as 'asc' | 'desc' | undefined) ?? 'desc';
  const statusFilter = getParam('status');
  const profileIdFilter = getParam('profileId');
  const deviceIdFilter = getParam('deviceId');
  const operatorIdFilter = getParam('operatorId');
  const fromFilter = getParam('from');
  const toFilter = getParam('to');
  const ch1Status = getParam('ch1Status');
  const ch2Status = getParam('ch2Status');
  const ch3Status = getParam('ch3Status');
  const ch4Status = getParam('ch4Status');
  const selected = getParam('selected');

  const handleRowClick = (test: Test) => {
    if (!canOpenTestResults(test.status)) return;
    setSelected(test.id);
    const fromParams = new URLSearchParams(location.search);
    fromParams.set('selected', test.id);
    const fromPath = `${location.pathname}?${fromParams.toString()}`;
    navigate(`/measurement/results/${test.id}?from=${encodeURIComponent(fromPath)}`);
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setParams({ sortBy: column, sortDir: sortDir === 'asc' ? 'desc' : 'asc' });
    } else {
      setParams({ sortBy: column, sortDir: 'desc' });
    }
  };

  const { data: profiles = [] } = useQuery({
    queryKey: ['profiles-for-filter'],
    queryFn: () => profileService.getProfiles(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: operatorsResponse } = useQuery({
    queryKey: ['operators-for-filter'],
    queryFn: () => usersApi.getList({ status: 'active', pageSize: 100 }),
    staleTime: 5 * 60 * 1000,
  });
  const operators = operatorsResponse?.items ?? [];

  const { data: devices = [] } = useQuery({
    queryKey: ['devices-for-filter'],
    queryFn: () => devicesApiService.getDevices(),
    staleTime: 5 * 60 * 1000,
  });

  // Draft state for filters that batch multiple fields behind a single Apply action
  const [dateFromDraft, setDateFromDraft] = useState('');
  const [dateToDraft, setDateToDraft] = useState('');
  const [channelDraft, setChannelDraft] = useState({ ch1: '', ch2: '', ch3: '', ch4: '' });

  const resyncDateDraft = () => {
    setDateFromDraft(fromFilter ?? '');
    setDateToDraft(toFilter ?? '');
  };

  const resyncChannelDraft = () => {
    setChannelDraft({
      ch1: ch1Status ?? '',
      ch2: ch2Status ?? '',
      ch3: ch3Status ?? '',
      ch4: ch4Status ?? '',
    });
  };

  const { data, isLoading, error, refetch } = useQuery<PagedResult>({
    queryKey: [
      'tests', page, pageSize, sortBy, sortDir, statusFilter, profileIdFilter,
      deviceIdFilter, operatorIdFilter, fromFilter, toFilter,
      ch1Status, ch2Status, ch3Status, ch4Status,
    ],
    queryFn: async () => {
      const response = await apiClient.get<PagedResult>('/api/v1/tests', {
        params: {
          page: page + 1,
          pageSize,
          ...(sortBy ? { sortBy } : {}),
          ...(sortBy ? { sortDir } : {}),
          ...(statusFilter ? { status: statusFilter } : {}),
          ...(profileIdFilter ? { profileId: profileIdFilter } : {}),
          ...(deviceIdFilter ? { deviceId: deviceIdFilter } : {}),
          ...(operatorIdFilter ? { operatorId: operatorIdFilter } : {}),
          ...(fromFilter ? { from: fromFilter } : {}),
          ...(toFilter ? { to: toFilter } : {}),
          ...(ch1Status ? { ch1Status } : {}),
          ...(ch2Status ? { ch2Status } : {}),
          ...(ch3Status ? { ch3Status } : {}),
          ...(ch4Status ? { ch4Status } : {}),
        },
      });
      return response.data;
    },
  });

  const tests = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;

  const handleOpenDelete = (e: React.MouseEvent, testId: string) => {
    e.stopPropagation();
    setDeleteDialog({ open: true, testId });
  };

  const handleDeleteSuccess = () => {
    setSnackbar({ open: true, message: 'Test deleted successfully', severity: 'success' });
    queryClient.invalidateQueries({ queryKey: ['tests'] });
  };

  const handleDeleteError = (message: string) => {
    setSnackbar({ open: true, message, severity: 'error' });
  };

  const handleRetrigger = async (e: React.MouseEvent, testId: string) => {
    e.stopPropagation();
    setRetriggering(testId);
    try {
      await apiClient.post(`/api/v1/tests/${testId}/classification/retrigger`);
      setSnackbar({ open: true, message: 'Classification re-triggered successfully', severity: 'success' });
      queryClient.invalidateQueries({ queryKey: ['tests'] });
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Failed to re-trigger classification';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setRetriggering(null);
    }
  };

  const handleStartClassification = async (e: React.MouseEvent, testId: string) => {
    e.stopPropagation();
    setStartingClassification(testId);
    try {
      await apiClient.post(`/api/v1/tests/${testId}/classification/start`);
      setSnackbar({ open: true, message: 'Classification started successfully', severity: 'success' });
      queryClient.invalidateQueries({ queryKey: ['tests'] });
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Failed to start classification';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setStartingClassification(null);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangePageSize = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Tests
        </Typography>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={() => refetch()}>
            Retry
          </Button>
        }>
          Failed to load tests
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Tests</Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => refetch()}
        >
          Refresh
        </Button>
      </Box>

      {tests.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No tests found. Create your first test to see it here.
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <TableSortLabel
                        active={sortBy === 'date'}
                        direction={sortBy === 'date' ? sortDir : 'desc'}
                        onClick={() => handleSort('date')}
                      >
                        Date/Time
                      </TableSortLabel>
                      <ColumnFilterMenu
                        active={!!fromFilter || !!toFilter}
                        label="Filter by date"
                        onOpen={resyncDateDraft}
                      >
                        {(close) => (
                          <Stack spacing={1.5} sx={{ minWidth: 200 }}>
                            <TextField
                              label="From"
                              type="date"
                              size="small"
                              InputLabelProps={{ shrink: true }}
                              value={dateFromDraft}
                              onChange={(e) => setDateFromDraft(e.target.value)}
                            />
                            <TextField
                              label="To"
                              type="date"
                              size="small"
                              InputLabelProps={{ shrink: true }}
                              value={dateToDraft}
                              onChange={(e) => setDateToDraft(e.target.value)}
                            />
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Button
                                size="small"
                                onClick={() => {
                                  setParams({ from: null, to: null });
                                  setDateFromDraft('');
                                  setDateToDraft('');
                                  close();
                                }}
                              >
                                Clear
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => {
                                  setParams({
                                    from: dateFromDraft || null,
                                    to: dateToDraft || null,
                                  });
                                  close();
                                }}
                              >
                                Apply
                              </Button>
                            </Stack>
                          </Stack>
                        )}
                      </ColumnFilterMenu>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <TableSortLabel
                        active={sortBy === 'profile'}
                        direction={sortBy === 'profile' ? sortDir : 'desc'}
                        onClick={() => handleSort('profile')}
                      >
                        Profile
                      </TableSortLabel>
                      <ColumnFilterMenu active={!!profileIdFilter} label="Filter by profile">
                        {(close) => (
                          <FormControl size="small" fullWidth sx={{ minWidth: 180 }}>
                            <InputLabel id="profile-filter-label">Profile</InputLabel>
                            <Select
                              labelId="profile-filter-label"
                              label="Profile"
                              value={profileIdFilter ?? ''}
                              onChange={(e) => {
                                setParams({ profileId: e.target.value || null });
                                close();
                              }}
                            >
                              <MenuItem value="">All</MenuItem>
                              {profiles.map((p) => (
                                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </ColumnFilterMenu>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <TableSortLabel
                        active={sortBy === 'operator'}
                        direction={sortBy === 'operator' ? sortDir : 'desc'}
                        onClick={() => handleSort('operator')}
                      >
                        Operator
                      </TableSortLabel>
                      <ColumnFilterMenu active={!!operatorIdFilter} label="Filter by operator">
                        {(close) => (
                          <FormControl size="small" fullWidth sx={{ minWidth: 180 }}>
                            <InputLabel id="operator-filter-label">Operator</InputLabel>
                            <Select
                              labelId="operator-filter-label"
                              label="Operator"
                              value={operatorIdFilter ?? ''}
                              onChange={(e) => {
                                setParams({ operatorId: e.target.value || null });
                                close();
                              }}
                            >
                              <MenuItem value="">All</MenuItem>
                              {operators.map((o) => (
                                <MenuItem key={o.id} value={o.id}>{o.fullName || o.email}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </ColumnFilterMenu>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <TableSortLabel
                        active={sortBy === 'device'}
                        direction={sortBy === 'device' ? sortDir : 'desc'}
                        onClick={() => handleSort('device')}
                      >
                        Device
                      </TableSortLabel>
                      <ColumnFilterMenu active={!!deviceIdFilter} label="Filter by device">
                        {(close) => (
                          <FormControl size="small" fullWidth sx={{ minWidth: 180 }}>
                            <InputLabel id="device-filter-label">Device</InputLabel>
                            <Select
                              labelId="device-filter-label"
                              label="Device"
                              value={deviceIdFilter ?? ''}
                              onChange={(e) => {
                                setParams({ deviceId: e.target.value || null });
                                close();
                              }}
                            >
                              <MenuItem value="">All</MenuItem>
                              {devices.map((d) => (
                                <MenuItem key={d.id} value={d.id}>{d.nickname || d.deviceUid}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </ColumnFilterMenu>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <TableSortLabel
                        active={sortBy === 'status'}
                        direction={sortBy === 'status' ? sortDir : 'desc'}
                        onClick={() => handleSort('status')}
                      >
                        Status
                      </TableSortLabel>
                      <ColumnFilterMenu active={!!statusFilter} label="Filter by status">
                        {(close) => (
                          <FormControl size="small" fullWidth sx={{ minWidth: 180 }}>
                            <InputLabel id="status-filter-label">Status</InputLabel>
                            <Select
                              labelId="status-filter-label"
                              label="Status"
                              value={statusFilter ?? ''}
                              onChange={(e) => {
                                setParams({ status: e.target.value || null });
                                close();
                              }}
                            >
                              <MenuItem value="">All</MenuItem>
                              {Object.values(TestStatus).map((s) => (
                                <MenuItem key={s} value={s}>{TestStatusLabels[s]}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </ColumnFilterMenu>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <span>Pathogens</span>
                      <ColumnFilterMenu
                        active={!!ch1Status || !!ch2Status || !!ch3Status || !!ch4Status}
                        label="Filter by channel result"
                        onOpen={resyncChannelDraft}
                      >
                        {(close) => (
                          <Stack spacing={1.5} sx={{ minWidth: 220 }}>
                            {([1, 2, 3, 4] as const).map((n) => {
                              const key = `ch${n}` as 'ch1' | 'ch2' | 'ch3' | 'ch4';
                              return (
                                <FormControl key={n} size="small" fullWidth>
                                  <InputLabel id={`ch${n}-filter-label`}>{`CH${n}`}</InputLabel>
                                  <Select
                                    labelId={`ch${n}-filter-label`}
                                    label={`CH${n}`}
                                    value={channelDraft[key]}
                                    onChange={(e) =>
                                      setChannelDraft((d) => ({ ...d, [key]: e.target.value }))
                                    }
                                  >
                                    <MenuItem value="">Don't care</MenuItem>
                                    <MenuItem value="positive">Positive</MenuItem>
                                    <MenuItem value="negative">Negative</MenuItem>
                                    <MenuItem value="inconclusive">Inconclusive</MenuItem>
                                    <MenuItem value="rejected">Rejected</MenuItem>
                                  </Select>
                                </FormControl>
                              );
                            })}
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Button
                                size="small"
                                onClick={() => {
                                  setParams({ ch1Status: null, ch2Status: null, ch3Status: null, ch4Status: null });
                                  setChannelDraft({ ch1: '', ch2: '', ch3: '', ch4: '' });
                                  close();
                                }}
                              >
                                Clear
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => {
                                  setParams({
                                    ch1Status: channelDraft.ch1 || null,
                                    ch2Status: channelDraft.ch2 || null,
                                    ch3Status: channelDraft.ch3 || null,
                                    ch4Status: channelDraft.ch4 || null,
                                  });
                                  close();
                                }}
                              >
                                Apply
                              </Button>
                            </Stack>
                          </Stack>
                        )}
                      </ColumnFilterMenu>
                    </Stack>
                  </TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {tests.map((test) => {
                  const canOpen = canOpenTestResults(test.status);
                  const blockReason = getTestOpeningBlockReason(test.status);
                  
                  return (
                  <TableRow
                    key={test.id}
                    data-testid={`test-row-${test.id}`}
                    hover={canOpen}
                    onClick={() => handleRowClick(test)}
                    sx={{
                      cursor: canOpen ? 'pointer' : 'not-allowed',
                      opacity: canOpen ? 1 : 0.6,
                      ...(test.id === selected ? { bgcolor: 'action.selected' } : {}),
                    }}
                    title={!canOpen ? blockReason || '' : undefined}
                  >
                    <TableCell>{formatDateTime(test.startedAt)}</TableCell>
                    <TableCell>{test.profileName || '-'}</TableCell>
                    <TableCell>{test.operatorName || '-'}</TableCell>
                    <TableCell>
                      {(() => {
                        const label = test.deviceNickname || test.deviceUid;
                        if (!label) return '-';
                        const truncated = label.length > 12 ? `${label.slice(0, 12)}…` : label;
                        return label.length > 12
                          ? <Tooltip title={label}><span>{truncated}</span></Tooltip>
                          : truncated;
                      })()}
                    </TableCell>
                    <TableCell>
                      <TestStatusBadge status={test.status} />
                    </TableCell>
                    <TableCell>
                      {test.pathogenClassificationResults &&
                      test.pathogenClassificationResults.length > 0 ? (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                          {[...test.pathogenClassificationResults]
                            .sort((a, b) => a.channelId - b.channelId)
                            .map((result) => {
                            const statusIcon = getStatusIcon(result.status);
                            return (
                              <Chip
                                key={`${result.channelId}-${result.pathogenName || result.channelId}`}
                                label={result.pathogenName || `Channel ${result.channelId}`}
                                icon={statusIcon || undefined}
                                color={getStatusColor(result.status)}
                                size="small"
                                variant={result.status ? 'filled' : 'outlined'}
                                title={
                                  result.status
                                    ? `${result.status.toUpperCase()}: ${(result.confidence * 100).toFixed(1)}%`
                                    : 'Pending classification'
                                }
                              />
                            );
                          })}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatDuration(test.durationMs)}</TableCell>
                    <TableCell
                      onClick={(e) => e.stopPropagation()}
                      sx={{ width: 96, p: '4px 8px' }}
                    >
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {canManageClassification && canStartClassification(test.status) && (
                          <Tooltip title="Start classification">
                            <span>
                              <IconButton
                                size="small"
                                onClick={(e) => handleStartClassification(e, test.id)}
                                disabled={startingClassification === test.id}
                                color="primary"
                              >
                                {startingClassification === test.id
                                  ? <CircularProgress size={16} />
                                  : <StartClassifyIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {canManageClassification && canRetriggerClassification(test.status) && (
                          <Tooltip title={test.status === 'classification_failed' ? 'Classification failed — re-trigger' : 'Re-trigger classification'}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={(e) => handleRetrigger(e, test.id)}
                                disabled={retriggering === test.id}
                                color={test.status === 'classification_failed' ? 'warning' : 'primary'}
                              >
                                {retriggering === test.id
                                  ? <CircularProgress size={16} />
                                  : <RetriggerIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {canViewChart && canOpen && (
                          <Tooltip title="View Chart">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                setChartOverlayTestId(test.id);
                              }}
                            >
                              <BarChartIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDeleteTests && (
                          <Tooltip title="Delete test">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => handleOpenDelete(e, test.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangePageSize}
          />
        </Paper>
      )}
      <DeleteTestDialog
        open={deleteDialog.open}
        testId={deleteDialog.testId}
        onClose={() => setDeleteDialog((d) => ({ ...d, open: false }))}
        onSuccess={handleDeleteSuccess}
        onError={handleDeleteError}
      />
      <TestChartOverlay
        open={chartOverlayTestId !== null}
        testId={chartOverlayTestId}
        onClose={() => setChartOverlayTestId(null)}
        pathogenClassificationResults={
          tests.find((t) => t.id === chartOverlayTestId)?.pathogenClassificationResults
        }
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={snackbar.severity === 'error' ? { sx: { bgcolor: 'error.main' } } : undefined}
      />
    </Box>
  );
}
